import { Button } from '@/components/ui/button'
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar'
import { Badge } from '@/components/ui/badge'
import { ArrowLeft, Bot } from 'lucide-react'
import type { Member as MemberType } from '@/types/member'

interface PrivateChatHeaderProps {
  member: MemberType
  isOnline?: boolean
  onBackToGroup: () => void
  // Mestre respondendo como NPC (o único indicador de estado que o header usa)
  respondingAsNpc?: {
    isActive: boolean
    npcName: string | null
  }
}

export function PrivateChatHeader({
  member,
  isOnline = false,
  onBackToGroup,
  respondingAsNpc
}: PrivateChatHeaderProps) {
  const isNpc = member.isNpc === true

  return (
    <div className="flex items-center gap-3 p-4 bg-game-secondary dark:bg-zinc-900 border-b border-game-border-primary dark:border-zinc-800 shadow-sm">
      {/* Botão Voltar */}
      <Button
        variant="ghost"
        size="icon"
        onClick={onBackToGroup}
        className="h-10 w-10 text-game-muted hover:text-game-primary dark:hover:text-zinc-100 hover:bg-game-hover dark:hover:bg-zinc-800 rounded-full flex-shrink-0"
      >
        <ArrowLeft className="h-5 w-5" />
      </Button>

      {/* Avatar do destinatário */}
      <div className="relative">
        <Avatar className="h-10 w-10 ring-2 ring-game-border-primary dark:ring-zinc-800">
          {isNpc ? (
            <AvatarFallback className="bg-purple-600/30 text-purple-400 font-medium text-sm">
              <Bot className="h-5 w-5" />
            </AvatarFallback>
          ) : (
            <>
              <AvatarImage src={member.imageUrl} alt={member.userFullName} />
              <AvatarFallback className="bg-game-tertiary dark:bg-zinc-800 text-game-primary dark:text-zinc-100 font-medium text-sm">
                {member.userFullName[0]?.toUpperCase() || '?'}
              </AvatarFallback>
            </>
          )}
        </Avatar>

        {/* Indicador de status online (NPCs sempre online) */}
        {(isOnline || isNpc) && (
          <div className={`absolute -bottom-0.5 -right-0.5 h-3 w-3 rounded-full border-2 border-game-secondary dark:border-zinc-900 ${
            isNpc ? 'bg-purple-500' : 'bg-emerald-500'
          }`} />
        )}
      </div>

      {/* Informações do usuário */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-1">
          <h2 className="font-semibold text-base text-game-primary dark:text-zinc-100 truncate">
            {member.userFullName}
          </h2>

          {/* Badge do papel */}
          <Badge
            variant="secondary"
            className={`text-xs font-medium ${
              isNpc
                ? 'bg-purple-600/20 text-purple-400 border-purple-500/30'
                : member.role === 'mestre'
                  ? 'bg-yellow-600/20 text-yellow-400 border-yellow-500/30'
                  : 'bg-blue-600/20 text-blue-400 border-blue-500/30'
            }`}
          >
            {isNpc ? 'NPC' : member.role === 'mestre' ? 'Mestre' : 'Jogador'}
          </Badge>
        </div>

        {/* Status de atividade */}
        <div className="flex items-center gap-2 text-xs">
          {isNpc ? (
            <span className="text-purple-400">Personagem não-jogador (NPC)</span>
          ) : isOnline ? (
            <span className="text-emerald-600 dark:text-emerald-400">online</span>
          ) : (
            <span className="text-game-muted">última vez há pouco</span>
          )}
        </div>

        {/* Indicador de "Respondendo como NPC" para o mestre */}
        {respondingAsNpc?.isActive && respondingAsNpc.npcName && (
          <div className="flex items-center gap-1 text-xs text-purple-400 bg-purple-600/20 px-2 py-0.5 rounded-full mt-1">
            <Bot className="h-3 w-3" />
            <span>Respondendo como {respondingAsNpc.npcName}</span>
          </div>
        )}
      </div>
    </div>
  )
}
